import { FC } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from 'jbc-front/components/Button';
import { FormErrors } from '@/components/form/FormErrors';
import { useSession } from '@/components/SessionProvider';
import { Headding, Item, Panel } from '@/components/PrimaryPanel';
import { Grid } from '@/components/ui/Grid';
import { Dd, Dl, Dt } from '@/components/ui/DefinitionList';
import { displayFormat, fullNameFormat } from '@/components/utils/formatter';
import { useMutation } from '@/hooks/graphql/useMutation';
import { useNotify } from '@/hooks/useNotify';
import { parseBackUrl } from '@/utils/url';
import { extractRoles } from '../../../utils';
import { User, ChangeRoleQueryVariables, CHANGE_ROLE } from '../../query';
import { PermissionForm } from '../PermissionForm/PermissionForm';
import { RoleProhibit } from '../PermissionForm/roleOptions';
import { Schema, schema } from '../PermissionForm/schema';
import styles from './EditForm.scss';

interface EditFormProps {
  user: User;
  lmsUserRole?: string;
}

export const EditForm: FC<EditFormProps> = ({ user, lmsUserRole }) => {
  const notify = useNotify();
  const { user: currentUser } = useSession();
  const location = useLocation();
  const history = useHistory();

  const methods = useForm<Schema>({
    resolver: yupResolver(schema),
    context: { currentUser },
    defaultValues: extractRoles(user.roles, RoleProhibit)
  });
  const {
    handleSubmit,
    setError,
    formState: { errors }
  } = methods;

  const [changeRole] = useMutation<unknown, ChangeRoleQueryVariables>(CHANGE_ROLE);

  const onSubmit: SubmitHandler<Schema> = async values => {
    const roleCodes = [values.stressCheckRole, values.healthCheckRole, values.userManageRole].filter(
      (v): v is string => v !== undefined && v !== null && v !== RoleProhibit
    );
    if (roleCodes.length === 0) {
      setError('root.roleCodes', {
        type: 'manual',
        message: 'すべての権限を「権限なし」にはできません。少なくとも1つ以上の権限を「権限あり」に設定してください。'
      });
      return;
    }

    await changeRole({
      variables: {
        input: {
          userRoleChanges: [
            {
              id: user.id,
              roleCodes,
              delete: false
            }
          ]
        }
      }
    });

    notify('更新しました', 'success');
    history.push(parseBackUrl(location, '/settings/users'));
  };

  return (
    <FormProvider {...methods}>
      {errors.root?.roleCodes?.message && (
        <FormErrors errors={[{ path: 'roleCodes', message: errors.root.roleCodes.message }]} />
      )}

      <Panel>
        <Headding>権限を編集</Headding>
        <Item>
          <Grid className="u-mb20">
            <Dl>
              <Dt>スタッフコード</Dt>
              <Dd>{user.employee ? displayFormat(user.employee?.staffCode) : '従業員登録なし'}</Dd>
            </Dl>
            <Dl>
              <Dt>氏名</Dt>
              <Dd>
                {user.employee
                  ? fullNameFormat(user.employee?.lastName, user.employee?.firstName)
                  : displayFormat(user.name)}
              </Dd>
            </Dl>
            <Dl>
              <Dt>メールアドレス</Dt>
              <Dd>{displayFormat(user.email)}</Dd>
            </Dl>
            <Dl>
              <Dt>労務権限</Dt>
              <Dd>{displayFormat(lmsUserRole)}</Dd>
            </Dl>
          </Grid>
          <FormProvider {...methods}>
            <PermissionForm />
          </FormProvider>
        </Item>
      </Panel>
      <div className={styles.footer}>
        <Button as={Link} to={parseBackUrl(location, '/settings/users')}>
          キャンセル
        </Button>
        <Button primary onClick={handleSubmit(onSubmit)}>
          保存
        </Button>
      </div>
    </FormProvider>
  );
};
